import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { avatarColor } from "../lib/colors";
import { IconCheck, IconClose } from "./icons";

const layerStack: number[] = [];
let layerSeq = 0;

/** Registers an overlay; only the topmost receives Escape. */
function useLayer(onClose: () => void) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const id = ++layerSeq;
    layerStack.push(id);
    const fn = (e: KeyboardEvent) => {
      if (e.key === "Escape" && layerStack[layerStack.length - 1] === id) {
        e.stopPropagation();
        e.preventDefault();
        closeRef.current();
      }
    };
    window.addEventListener("keydown", fn, true);
    return () => {
      window.removeEventListener("keydown", fn, true);
      const i = layerStack.indexOf(id);
      if (i >= 0) layerStack.splice(i, 1);
    };
  }, []);
}

export const hasOpenLayer = () => layerStack.length > 0;

export function Modal({
  onClose,
  children,
  className,
  center,
  label,
}: {
  onClose: () => void;
  children: ReactNode;
  className?: string;
  center?: boolean;
  label?: string;
}) {
  useLayer(onClose);
  return createPortal(
    <div
      className={`modal-layer${center ? " center" : ""}`}
      onMouseDown={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
      // Portals bubble React events to their owner; keep clicks/keys inside the modal.
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className={`modal ${className ?? ""}`} role="dialog" aria-modal="true" aria-label={label}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export interface Anchor {
  getBoundingClientRect(): DOMRect;
}

/** A floating panel positioned under (or above) an anchor element. */
export function Popover({
  anchor,
  onClose,
  children,
  className,
  placement = "bottom-start",
}: {
  anchor: Anchor | null;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  placement?: "bottom-start" | "bottom-end";
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayer(onClose);
  useLayoutEffect(() => {
    if (!anchor || !ref.current) return;
    const r = anchor.getBoundingClientRect();
    const el = ref.current.getBoundingClientRect();
    let left = placement === "bottom-end" ? r.right - el.width : r.left;
    let top = r.bottom + 4;
    if (top + el.height > window.innerHeight - 8) top = Math.max(8, r.top - el.height - 4);
    left = Math.max(8, Math.min(left, window.innerWidth - el.width - 8));
    setPos({ top, left });
  }, [anchor, placement]);
  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const t = setTimeout(() => document.addEventListener("mousedown", fn), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", fn);
    };
  }, [onClose]);
  return createPortal(
    <div
      ref={ref}
      className={`popover ${className ?? ""}`}
      style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}

/** Anchor state helper: `open(e)` stores the clicked element. */
export function useAnchor<T extends Element = HTMLElement>() {
  const [anchor, setAnchor] = useState<T | null>(null);
  return {
    anchor,
    open: (e: { currentTarget: EventTarget }) => setAnchor(e.currentTarget as T),
    openAt: (el: T | null) => setAnchor(el),
    close: () => setAnchor(null),
  };
}

export interface MenuEntry {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  checked?: boolean;
  separator?: boolean;
}

export function Menu({
  anchor,
  onClose,
  items,
  placement,
}: {
  anchor: Anchor | null;
  onClose: () => void;
  items: MenuEntry[];
  placement?: "bottom-start" | "bottom-end";
}) {
  return (
    <Popover anchor={anchor} onClose={onClose} placement={placement}>
      <div role="menu">
        {items.map((it) =>
          it.separator ? (
            <div key={it.key} className="menu-sep" />
          ) : (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              className={`menu-item${it.danger ? " danger" : ""}`}
              onClick={() => {
                onClose();
                it.onSelect?.();
              }}
            >
              {it.icon}
              <span>{it.label}</span>
              {it.checked && (
                <span className="check">
                  <IconCheck size={16} />
                </span>
              )}
            </button>
          ),
        )}
      </div>
    </Popover>
  );
}

export function Avatar({ name, size = 24, title }: { name: string; size?: number; title?: string }) {
  const initial = (name.trim()[0] ?? "?").toUpperCase();
  return (
    <span
      className="avatar"
      title={title ?? name}
      style={{ width: size, height: size, background: avatarColor(name), fontSize: Math.round(size * 0.45) }}
    >
      {initial}
    </span>
  );
}

export function ModalHeader({ title, onClose }: { title: ReactNode; onClose: () => void }) {
  return (
    <div className="modal-header">
      <span>{title}</span>
      <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
        <IconClose size={20} />
      </button>
    </div>
  );
}

export function Confirm({
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onClose,
}: {
  message: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal onClose={onClose} center className="confirm" label="confirm">
      <div className="modal-body" style={{ fontSize: 14 }}>
        {message}
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          {cancelLabel}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          autoFocus
          onClick={() => {
            onClose();
            onConfirm();
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
